import * as React from 'react'
import { snapshotToData } from './helper'
import useIsEqualRef from './util/useIsEqualRef'
import usePaginationValue, { PaginationHook } from './util/usePaginationValue'
import { query as firebaseQuery, startAt, limit, onSnapshot } from '~providers/firebase'
import { Query, DocumentSnapshot, SnapshotListenOptions } from 'firebase/firestore'

const { useEffect, useMemo } = React

const DEFAULT_LIMIT = 20

export const usePagination = <T = DocumentSnapshot>(
  query?: Query | null,
  options?: {
    snapshotListenOptions?: SnapshotListenOptions
    limit?: number
  },
): PaginationHook<T> => {
  const { loaded, loadingMore, error, setError, setValue, reest, value, after, loadMore, hasMore } =
    usePaginationValue()

  const ref = useIsEqualRef(query, reest)

  useEffect(() => {
    if (!ref.current) {
      return
    }

    const stepLimit = options?.limit || DEFAULT_LIMIT
    let queryLimited = ref.current

    // If not the first query then pass startAt and add an extra limit (startAt is inclusive but we could have duplicates)
    if (after !== null) {
      queryLimited = firebaseQuery(queryLimited, startAt(after), limit(stepLimit + 1))
    } else {
      queryLimited = firebaseQuery(queryLimited, limit(stepLimit))
    }

    const snapshotOption = options?.snapshotListenOptions
    const listener = snapshotOption
      ? onSnapshot(queryLimited, snapshotOption, setValue(stepLimit), setError)
      : onSnapshot(queryLimited, setValue(stepLimit), setError)

    return () => listener()
  }, [ref.current, after])

  return [
    value,
    {
      loaded,
      loadingMore,
      hasMore,
      loadMore,
    },
    error,
  ]
}

export const usePaginationData = <T>(
  query?: Query | null,
  options?: {
    idField?: string
    limit?: number
    snapshotListenOptions?: SnapshotListenOptions
  },
): PaginationHook<T> => {
  const idField = options ? options.idField : undefined

  const [snapshot, fields, error] = usePagination(query, {
    snapshotListenOptions: options?.snapshotListenOptions,
    limit: options?.limit,
  })
  const values = useMemo(
    () => (snapshot ? snapshot.map((doc) => snapshotToData(doc, idField)) : undefined) as T[],
    [snapshot, idField],
  )
  return [values, fields, error]
}
